import React from 'react';
import PropTypes from 'prop-types';

/**
 * A card component displaying the user's electricity rationing information
 * 
 * @param {Object} props - Component props
 * @param {string} props.rationStatus - Current rationing status (ACTIVE, SUSPENDED, etc.)
 * @param {number} props.appliedRation - Amount of ration applied
 * @param {number} props.remainingRation - Amount of ration remaining
 * @param {string} props.lastUpdated - Timestamp of last update
 * @param {number} props.percentRemaining - Percentage of ration remaining
 */
const RationingCard = ({ 
  rationStatus = 'SUSPENDED', 
  appliedRation = 0, 
  remainingRation = 0, 
  lastUpdated = '', 
  percentRemaining = 0 
}) => {
  // Format the date to be more readable
  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    const date = new Date(dateString);
    return date.toLocaleString('en-ZA', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Determine status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'text-yellow-600';
      case 'SUSPENDED':
        return 'text-green-600';
      case 'EXHAUSTED':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  // Get battery fill color based on percentage
  const getBatteryColor = (percentage) => {
    if (percentage > 60) return 'bg-green-500';
    if (percentage > 25) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const statusColor = getStatusColor(rationStatus);
  const batteryColor = getBatteryColor(percentRemaining);

  return (
    <div className="card bg-white shadow-md rounded-lg p-6">
      <h2 className="text-gray-500 text-sm font-semibold uppercase mb-2 font-mont">Electricity Rationing</h2>
      <div className="flex items-baseline mb-4">
        <span className="text-xl font-bold text-hooray-dark-text font-mont">
          Ration Information
        </span>
      </div>
      
      <div className="mt-2 space-y-2">
        <div className="flex justify-between">
          <span className="text-gray-600">Status:</span>
          <span className={`font-semibold ${statusColor}`}>
            {rationStatus}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Applied Ration:</span>
          <span className="font-semibold">
            {typeof appliedRation === 'number' ? appliedRation.toFixed(2) : '0.00'} kWh
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Remaining:</span>
          <span className="font-semibold">
            {typeof remainingRation === 'number' ? remainingRation.toFixed(2) : '0.00'} kWh
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Percentage:</span>
          <span className="font-semibold">
            {typeof percentRemaining === 'number' ? percentRemaining.toFixed(0) : '0'}%
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Last Updated:</span>
          <span className="font-semibold text-sm">
            {formatDate(lastUpdated)}
          </span>
        </div>
      </div>
      
      {/* Battery indicator for active rationing */}
      {rationStatus === 'ACTIVE' && (
        <div className="mt-6 flex justify-center">
          {/* Battery visualization */}
          <div className="relative w-32 h-16 border-2 border-gray-400 rounded-md overflow-hidden">
            {/* Battery tip */}
            <div className="absolute -right-1 top-1/3 bottom-1/3 w-2 bg-gray-400 rounded-r-sm"></div>
            
            {/* Battery fill */}
            <div 
              className={`absolute left-0 bottom-0 top-0 ${batteryColor} transition-all duration-300 ease-in-out`}
              style={{ width: `${Math.max(5, percentRemaining)}%` }}
            ></div>
            
            {/* Percentage text */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="font-bold text-lg text-black drop-shadow-md">
                {Math.round(percentRemaining)}%
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* Ration progress bar for active rationing */}
      {rationStatus === 'ACTIVE' && (
        <div className="mt-4">
          <div className="h-2 w-full bg-gray-200 rounded-full">
            <div 
              className={`h-2 ${batteryColor} rounded-full`}
              style={{ width: `${percentRemaining}%` }}
            ></div>
          </div>
        </div>
      )}
      
      <div className="mt-4 text-sm text-gray-600">
        {rationStatus === 'SUSPENDED' 
          ? 'Rationing is currently suspended for your unit. You can use electricity normally.'
          : rationStatus === 'ACTIVE'
            ? 'WARNING: Rationing is currently active! Your electricity usage is limited. Please use electricity sparingly.'
            : 'Your electricity rationing status is currently being determined.'}
      </div>
    </div>
  );
};

RationingCard.propTypes = {
  rationStatus: PropTypes.string,
  appliedRation: PropTypes.number,
  remainingRation: PropTypes.number,
  lastUpdated: PropTypes.string,
  percentRemaining: PropTypes.number
};

export default RationingCard; 